import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { nps } from '@/utils/api';
import { Star, Check } from 'lucide-react';

export default function NpsAnswer() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState(null);
  const [score, setScore] = useState(null);
  const [comment, setComment] = useState('');
  const [metrics, setMetrics] = useState('');
  const [benefits, setBenefits] = useState('');
  const [consentPublic, setConsentPublic] = useState(false);
  const [saved, setSaved] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    nps.publicGet(token).then(r => {
      setState(r.data);
      setSaved(r.data.answered);
    }).catch(() => setState({ error: true })).finally(() => setLoading(false));
  }, [token]);

  const submit = async () => {
    if (score === null) return;
    setSubmitting(true);
    try {
      await nps.publicAnswer(token, { score, comment, metrics, benefits, consent_public: consentPublic });
      setSaved(true);
    } catch {} finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ background: '#FAF8F4' }}>Loading…</div>;
  if (state?.error) return <div className="min-h-screen flex items-center justify-center text-center p-6" style={{ background: '#FAF8F4' }}>
    <p style={{ color: '#B34A3A' }}>Survey link not found.</p>
  </div>;

  const brandColor = state?.brand?.primary_color || '#006080';

  if (saved) return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#FAF8F4' }}>
      <div className="max-w-md w-full text-center bg-white rounded-card p-8 shadow" data-testid="nps-thanks">
        <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: brandColor + '20' }}>
          <Check size={24} style={{ color: brandColor }} />
        </div>
        <h2 className="font-black text-xl mb-1">Thank you!</h2>
        <p className="text-sm" style={{ color: '#5C6B72' }}>Your feedback helps us improve.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#FAF8F4', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
      <div className="max-w-xl w-full bg-white rounded-card p-8 shadow" data-testid="nps-form">
        {state.brand?.name && (
          <p className="text-xs uppercase tracking-wider font-bold mb-1" style={{ color: brandColor }}>{state.brand.name}</p>
        )}
        <h1 className="text-2xl font-black mb-2" style={{ color: '#1A2428' }}>
          How likely are you to recommend {state.brand?.name || 'us'}?
        </h1>
        <p className="text-sm mb-6" style={{ color: '#5C6B72' }}>
          Project: <b>{state.project_name}</b>
        </p>

        <div className="flex gap-1 mb-5 justify-between" data-testid="nps-scale">
          {Array.from({ length: 11 }, (_, i) => (
            <button
              key={i}
              onClick={() => setScore(i)}
              className="flex-1 h-12 rounded-btn font-bold text-sm transition-all"
              style={{
                background: score === i ? brandColor : 'white',
                color: score === i ? 'white' : '#1A2428',
                border: `1px solid ${score === i ? brandColor : '#E8E3D9'}`,
              }}
              data-testid={`nps-score-${i}`}
            >
              {i}
            </button>
          ))}
        </div>
        <div className="flex justify-between text-xs mb-5" style={{ color: '#8A99A0' }}>
          <span>Not at all</span>
          <span>Extremely</span>
        </div>

        <textarea
          className="w-full p-3 rounded-btn border text-sm resize-none mb-4"
          style={{ borderColor: '#E8E3D9', background: '#FAF8F4' }}
          rows={3}
          placeholder="Anything you'd like us to know? (optional)"
          value={comment}
          onChange={e => setComment(e.target.value)}
          data-testid="nps-comment"
        />

        <div className="space-y-3 mb-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: '#5C6B72' }}>
              Did anything measurable improve? <span className="font-normal normal-case" style={{ color: '#8A99A0' }}>(optional)</span>
            </label>
            <input
              className="w-full p-3 rounded-btn border text-sm"
              style={{ borderColor: '#E8E3D9', background: '#FAF8F4' }}
              placeholder="e.g. cut invoice processing from 12h/wk to 30 min"
              value={metrics}
              onChange={e => setMetrics(e.target.value)}
              data-testid="nps-metrics"
            />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: '#5C6B72' }}>
              Biggest benefit to your team / business? <span className="font-normal normal-case" style={{ color: '#8A99A0' }}>(optional)</span>
            </label>
            <input
              className="w-full p-3 rounded-btn border text-sm"
              style={{ borderColor: '#E8E3D9', background: '#FAF8F4' }}
              placeholder="e.g. our ops lead got her Fridays back"
              value={benefits}
              onChange={e => setBenefits(e.target.value)}
              data-testid="nps-benefits"
            />
          </div>
        </div>

        {score !== null && score >= 8 && (comment.trim().length > 10 || metrics.trim().length > 5 || benefits.trim().length > 5) && (
          <label className="flex items-start gap-2 mb-4 text-xs p-3 rounded-btn" style={{ background: brandColor + '10', color: '#1A2428' }}>
            <input
              type="checkbox"
              className="mt-0.5 flex-shrink-0"
              checked={consentPublic}
              onChange={e => setConsentPublic(e.target.checked)}
              data-testid="nps-consent"
            />
            <span>
              It's okay to use this feedback as a public testimonial on {state.brand?.name || 'the'} website.
              You can withdraw consent any time.
            </span>
          </label>
        )}

        <button
          onClick={submit}
          disabled={score === null || submitting}
          className="w-full py-3 rounded-btn font-bold text-white text-sm transition-opacity disabled:opacity-50"
          style={{ background: brandColor }}
          data-testid="nps-submit"
        >
          {submitting ? 'Sending…' : 'Submit feedback'}
        </button>
      </div>
    </div>
  );
}
